import React, { useState } from 'react';

interface Step {
  id: number;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  details?: any;
  timestamp?: string;
  error?: string;
}

export const FolderCreationSteps: React.FC = () => {
  const [folderName, setFolderName] = useState('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [finalResult, setFinalResult] = useState<any>(null);

  const updateStep = (id: number, updates: Partial<Step>) => {
    setSteps(prev => prev.map(step =>
      step.id === id
        ? { ...step, ...updates, timestamp: new Date().toISOString() }
        : step
    ));
  };

  const addStep = (step: Omit<Step, 'id' | 'timestamp'>) => {
    const newStep = {
      ...step,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };
    setSteps(prev => [...prev, newStep]);
    return newStep.id;
  };

  const createFolder = async () => {
    if (!folderName.trim()) {
      alert('Please enter a folder name');
      return;
    }

    setIsProcessing(true);
    setSteps([]);
    setFinalResult(null);

    // STEP 1: Validate Input
    const step1Id = addStep({
      title: '1️⃣ Validate Input',
      description: 'Checking if folder name is valid',
      status: 'in-progress',
      details: { input: folderName }
    });

    await new Promise(resolve => setTimeout(resolve, 500));

    const validationChecks = {
      notEmpty: folderName.trim().length > 0,
      validChars: /^[a-zA-Z0-9\s\-_]+$/.test(folderName),
      lengthOk: folderName.length <= 50
    };

    updateStep(step1Id, {
      status: 'completed',
      details: {
        input: folderName,
        checks: validationChecks,
        valid: Object.values(validationChecks).every(v => v)
      }
    });

    // STEP 2: Generate Folder Path
    const step2Id = addStep({
      title: '2️⃣ Generate Folder Path',
      description: 'Converting name to URL-safe path',
      status: 'in-progress'
    });

    await new Promise(resolve => setTimeout(resolve, 500));

    const folderPath = folderName
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    updateStep(step2Id, {
      status: 'completed',
      details: {
        original: folderName,
        converted: folderPath,
        steps: [
          `1. Original: "${folderName}"`,
          `2. Lowercase: "${folderName.toLowerCase()}"`,
          `3. Replace special chars: "${folderName.toLowerCase().replace(/[^a-z0-9-]/g, '-')}"`,
          `4. Clean hyphens: "${folderPath}"`
        ]
      }
    });

    // STEP 3: Prepare API Request
    const step3Id = addStep({
      title: '3️⃣ Prepare API Request',
      description: 'Creating request payload',
      status: 'in-progress'
    });

    await new Promise(resolve => setTimeout(resolve, 300));

    const requestPayload = {
      clientName: folderName
    };

    updateStep(step3Id, {
      status: 'completed',
      details: {
        method: 'POST',
        url: 'http://localhost:8787/api/simple/folders/create',
        headers: {
          'Content-Type': 'application/json'
        },
        body: requestPayload
      }
    });

    // STEP 4: Send Request to API
    const step4Id = addStep({
      title: '4️⃣ Send Request to CloudFlare Worker',
      description: 'Making HTTP POST request',
      status: 'in-progress'
    });

    try {
      const startTime = Date.now();
      const response = await fetch('http://localhost:8787/api/simple/folders/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });
      const responseTime = Date.now() - startTime;

      updateStep(step4Id, {
        status: 'completed',
        details: {
          statusCode: response.status,
          statusText: response.statusText,
          responseTime: `${responseTime}ms`,
          headers: Object.fromEntries(response.headers.entries())
        }
      });

      // STEP 5: Parse Response
      const step5Id = addStep({
        title: '5️⃣ Parse API Response',
        description: 'Converting response to JSON',
        status: 'in-progress'
      });

      const data = await response.json();

      updateStep(step5Id, {
        status: 'completed',
        details: {
          success: data.success,
          responseData: data
        }
      });

      if (data.success) {
        // STEP 6: R2 Storage Operations
        const step6Id = addStep({
          title: '6️⃣ R2 Storage Operations',
          description: 'Files created in CloudFlare R2',
          status: 'completed',
          details: {
            bucketOperation: 'PUT',
            metadataKey: data.folder.metadataKey,
            folderStructure: `clients/${data.folder.folderPath}/`,
            metadata: {
              clientName: data.folder.clientName,
              folderPath: data.folder.folderPath,
              createdAt: new Date().toISOString()
            }
          }
        });

        // STEP 7: Generate Results
        const step7Id = addStep({
          title: '7️⃣ Generate Access URLs',
          description: 'Creating public and internal URLs',
          status: 'completed',
          details: {
            publicUrl: data.folder.url,
            r2Path: `clients/${data.folder.folderPath}/`,
            metadataPath: data.folder.metadataKey
          }
        });

        // STEP 8: Success
        const step8Id = addStep({
          title: '✅ Folder Created Successfully',
          description: 'All operations completed',
          status: 'completed',
          details: {
            folderName: data.folder.clientName,
            folderPath: data.folder.folderPath,
            url: data.folder.url,
            nextSteps: [
              'You can now upload files to this folder',
              'Share links will use this folder structure',
              'Files will be stored in R2 under this path'
            ]
          }
        });

        setFinalResult(data.folder);
      } else {
        throw new Error(data.error || 'Failed to create folder');
      }
    } catch (error: any) {
      updateStep(step4Id, {
        status: 'error',
        error: error.message
      });

      addStep({
        title: '❌ Error Occurred',
        description: error.message,
        status: 'error',
        details: {
          error: error.message,
          stack: error.stack
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const testListFolders = async () => {
    setIsProcessing(true);
    setSteps([]);
    setFinalResult(null);

    // STEP 1: Prepare List Request
    const step1Id = addStep({
      title: '1️⃣ Prepare List Request',
      description: 'Setting up API call to list folders',
      status: 'in-progress'
    });

    await new Promise(resolve => setTimeout(resolve, 300));

    updateStep(step1Id, {
      status: 'completed',
      details: {
        method: 'GET',
        url: 'http://localhost:8787/api/simple/folders/list',
        purpose: 'Retrieve all folders from R2'
      }
    });

    // STEP 2: Send Request
    const step2Id = addStep({
      title: '2️⃣ Query R2 Storage',
      description: 'Fetching folder list from CloudFlare R2',
      status: 'in-progress'
    });

    try {
      const response = await fetch('http://localhost:8787/api/simple/folders/list');
      const data = await response.json();

      updateStep(step2Id, {
        status: 'completed',
        details: {
          statusCode: response.status,
          foldersFound: data.count || 0
        }
      });

      // STEP 3: Process Results
      const step3Id = addStep({
        title: '3️⃣ Process R2 Results',
        description: 'Reading metadata for each folder',
        status: 'completed',
        details: {
          folders: data.folders,
          operations: data.folders?.map((f: any) => ({
            folder: f.folderPath,
            metadataRead: `clients/${f.folderPath}/metadata.json`,
            url: f.url
          }))
        }
      });

      setFinalResult(data);
    } catch (error: any) {
      addStep({
        title: '❌ Error Listing Folders',
        description: error.message,
        status: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🔍 Folder Creation - Every Single Step</h1>

        {/* Input Section */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Create New Folder</h2>
          <div className="flex gap-3">
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter client name (e.g., acme-corp)"
              className="flex-1 px-4 py-2 bg-gray-900 rounded border border-gray-700 text-white"
              disabled={isProcessing}
            />
            <button
              onClick={createFolder}
              disabled={!folderName.trim() || isProcessing}
              className="px-6 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
            >
              {isProcessing ? '⏳ Processing...' : '🚀 Create Folder'}
            </button>
            <button
              onClick={testListFolders}
              disabled={isProcessing}
              className="px-6 py-2 bg-green-600 rounded font-bold hover:bg-green-700 disabled:opacity-50"
            >
              📋 List All Folders
            </button>
          </div>
        </div>

        {/* Steps Visualization */}
        {steps.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold mb-4">Execution Steps:</h2>
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  step.status === 'completed' ? 'bg-green-900/20 border-green-600' :
                  step.status === 'in-progress' ? 'bg-blue-900/20 border-blue-600 animate-pulse' :
                  step.status === 'error' ? 'bg-red-900/20 border-red-600' :
                  'bg-gray-800 border-gray-700'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-bold text-lg">{step.title}</h3>
                    <p className="text-gray-400">{step.description}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${
                      step.status === 'completed' ? 'text-green-400' :
                      step.status === 'in-progress' ? 'text-blue-400' :
                      step.status === 'error' ? 'text-red-400' :
                      'text-gray-400'
                    }`}>
                      {step.status.toUpperCase()}
                    </span>
                    {step.timestamp && (
                      <p className="text-xs text-gray-500">
                        {new Date(step.timestamp).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                </div>

                {step.details && (
                  <div className="mt-3 p-3 bg-black/30 rounded font-mono text-xs overflow-auto">
                    <pre>{JSON.stringify(step.details, null, 2)}</pre>
                  </div>
                )}

                {step.error && (
                  <div className="mt-3 p-3 bg-red-900/30 rounded">
                    <p className="text-red-400">{step.error}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Final Result */}
        {finalResult && (
          <div className="mt-6 p-6 bg-green-900/30 border-2 border-green-600 rounded-lg">
            <h2 className="text-xl font-bold mb-4">🎉 Final Result</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Folder Name</p>
                <p className="font-bold">{finalResult.clientName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Folder Path</p>
                <p className="font-bold">{finalResult.folderPath || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Public URL</p>
                <p className="font-bold text-blue-400">{finalResult.url || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">R2 Metadata</p>
                <p className="font-bold text-xs">{finalResult.metadataKey || 'N/A'}</p>
              </div>
            </div>

            {finalResult.folders && (
              <div className="mt-4">
                <h3 className="font-bold mb-2">Found {finalResult.count} Folders:</h3>
                <div className="space-y-2">
                  {finalResult.folders.map((folder: any, i: number) => (
                    <div key={i} className="p-2 bg-black/30 rounded">
                      <p>{folder.clientName || folder.folderPath} - {folder.url}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Architecture Explanation */}
        <div className="mt-8 p-6 bg-gray-800 rounded-lg">
          <h2 className="text-xl font-bold mb-4">📐 Architecture Flow</h2>
          <pre className="text-xs font-mono text-gray-300">
{`
┌─────────────┐     ┌──────────────┐     ┌─────────────┐
│   Browser   │────▶│ CF Worker    │────▶│     R2      │
│  (React)    │◀────│   (Hono)     │◀────│  Storage    │
└─────────────┘     └──────────────┘     └─────────────┘
      │                    │                     │
      │                    │                     │
      ▼                    ▼                     ▼
  User Input         API Processing         File Storage
  Validation         Route Handling         Metadata.json
  UI Updates         JSON Response          Folder Structure
`}
          </pre>
        </div>
      </div>
    </div>
  );
};